import { useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Package,
  Wrench,
  Boxes,
  Calendar,
  Users,
  UserCog,
  Settings,
  LogOut,
  ClipboardCheck,
  MapPin,
  Ticket,
  Building2,
} from "lucide-react";
import { NavLink } from "@/shared/components";
import logo from "@/assets/logo.png";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarFooter,
  SidebarSeparator,
  useSidebar,
} from "@/shared/ui";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/components";
import { useAuthStore } from "@/features/auth";
import { authApi } from "@/entities/auth";
import { getDisplayName } from "@entities/user";
import { useUserScopeStore } from "@/features/auth/model/user-scope.store";
import { useImpersonationStore } from "@/features/user/model/impersonationStore";
import { workContextToBackendRole } from "@/features/user/utils/role-mapping";
import { toast } from "sonner";
import { LanguageSwitcher } from "@/shared/components";
import { isSystemAdmin, isTechnicalUser, isLocationUser } from "@/shared/types";

interface MenuItem {
  key: string;
  url: string;
  icon?: React.ComponentType<{ className?: string }>;
  children?: { key: string; url: string }[];
}

const TECHNICAL_ROLE_MENU: MenuItem[] = [
  { key: "technicianDashboard", url: "/technician-dashboard", icon: Wrench },
  {
    key: "tickets",
    url: "/tickets",
    icon: Ticket,
    children: [
      { key: "issueCategories", url: "/tickets/categories" },
      { key: "ticketQueues", url: "/tickets/queues" },
    ],
  },
];

/** Location User: only Location Dashboard, Tickets, and Events (no location switcher, no other areas). */
const LOCATION_USER_MENU: MenuItem[] = [
  { key: "technicianDashboard", url: "/technician-dashboard", icon: Wrench },
  { key: "tickets", url: "/tickets", icon: Ticket },
  { key: "events", url: "/events", icon: Calendar },
];

const getMenuStructure = (
  isSystemAdmin: boolean,
  isTechnicalUserRole: boolean,
  isLocationUserRole: boolean
): MenuItem[] => {
  // System admin only sees accounts
  if (isSystemAdmin) {
    return [{ key: "accounts", url: "/accounts", icon: Building2 }];
  }

  // Location User: only location dashboard, tickets, events
  if (isLocationUserRole) {
    return LOCATION_USER_MENU;
  }

  // Service Technician sees only technical dashboard and tickets.
  // Technical Manager falls through and is filtered by policy.
  if (isTechnicalUserRole) {
    return TECHNICAL_ROLE_MENU;
  }

  // Tenant users see all tenant-specific menus
  return [
    { key: "dashboard", url: "/", icon: LayoutDashboard },
    { key: "technicianDashboard", url: "/technician-dashboard", icon: Wrench },
    { key: "events", url: "/events", icon: Calendar },
    {
      key: "branches",
      url: "/branches",
      icon: MapPin,
      children: [{ key: "branchCategories", url: "/branches/categories" }],
    },
    {
      key: "assets",
      url: "/assets",
      icon: Package,
      children: [
        { key: "assetCategories", url: "/assets/categories" },
        { key: "assetModels", url: "/assets/models" },
      ],
    },
    { key: "maintenance", url: "/maintenance", icon: Wrench },
    { key: "inspections", url: "/inspections", icon: ClipboardCheck },
    {
      key: "parts",
      url: "/parts",
      icon: Boxes,
      children: [
        { key: "warehouses", url: "/warehouses" },
        { key: "suppliers", url: "/suppliers" },
      ],
    },
    {
      key: "tickets",
      url: "/tickets",
      icon: Ticket,
      children: [
        { key: "issueCategories", url: "/tickets/categories" },
        { key: "ticketQueues", url: "/tickets/queues" },
      ],
    },
    { key: "people", url: "/people", icon: Users },
    { key: "users", url: "/users", icon: UserCog },
    { key: "settings", url: "/settings", icon: Settings },
  ];
};

export function AppSidebar() {
  const { t } = useTranslation("navigation");
  const { t: tCommon } = useTranslation("common");
  const { open, isMobile, setOpenMobile } = useSidebar();
  const location = useLocation();
  const currentPath = location.pathname;
  const navigate = useNavigate();
  const { user: currentUser } = useAuthStore();
  const { canAccessMenuItem } = useUserScopeStore();
  const { isImpersonating, impersonatedContext, impersonatedUserName } = useImpersonationStore();

  // When impersonating, use impersonated user's role for menu and access; otherwise use auth user
  const effectiveRole =
    isImpersonating() && impersonatedContext
      ? workContextToBackendRole(impersonatedContext)
      : (currentUser?.role ?? null);

  const userIsSystemAdmin = isSystemAdmin(effectiveRole);
  const userIsTechnicalUser = isTechnicalUser(effectiveRole);
  const userIsLocationUser = isLocationUser(effectiveRole);
  const menuStructure = getMenuStructure(
    userIsSystemAdmin,
    userIsTechnicalUser,
    userIsLocationUser
  );

  // Filter menu items based on user scope. System admin, Service Technician, and Location User
  // get a fixed menu; everyone else (incl. Technical Manager) is filtered by policy.
  const filteredMenuStructure = userIsSystemAdmin
    ? menuStructure
    : userIsTechnicalUser || userIsLocationUser
      ? menuStructure
      : menuStructure
          .map((item) => {
            const canAccessParent = canAccessMenuItem(item.key);

            // If parent has children, filter them too
            if (item.children) {
              const filteredChildren = item.children.filter((child) =>
                canAccessMenuItem(child.key)
              );

              // Only include parent if it has accessible children or is accessible itself
              if (filteredChildren.length > 0 || canAccessParent) {
                return {
                  ...item,
                  children: filteredChildren,
                };
              }
              return null;
            }

            return canAccessParent ? item : null;
          })
          .filter((item): item is MenuItem => item !== null);

  const handleLogout = async () => {
    await authApi.logout();
    toast.success(tCommon("messages.logoutSuccess"));
    navigate("/auth");
  };

  const handleNavClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const isActive = (path: string) => {
    if (path === "/") {
      return currentPath === "/";
    }
    return currentPath.startsWith(path);
  };

  const isParentActive = (item: MenuItem) => {
    if (isActive(item.url)) return true;
    if (item.children) {
      return item.children.some((child) => isActive(child.url));
    }
    return false;
  };

  return (
    <Sidebar collapsible="icon">
      <div className="flex items-center gap-2 border-b px-4 py-[15px]">
        <img src={logo} alt="Lury OS Logo" className={open ? "h-8" : "h-8 w-8 object-contain"} />
      </div>
      <SidebarContent className="py-0">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {filteredMenuStructure.map((item) => (
                <SidebarMenuItem key={item.key}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={item.url}
                      end={item.url === "/" || !!item.children}
                      onClick={handleNavClick}
                      className="hover:bg-muted/50"
                      activeClassName="bg-muted text-primary font-medium"
                    >
                      {item.icon && <item.icon className="h-4 w-4" />}
                      {open && <span>{t(item.key)}</span>}
                    </NavLink>
                  </SidebarMenuButton>

                  {/* Render submenu if parent is active and sidebar is open */}
                  {item.children && open && isParentActive(item) && (
                    <SidebarMenuSub>
                      {item.children.map((child) => (
                        <SidebarMenuSubItem key={child.key}>
                          <SidebarMenuSubButton asChild>
                            <NavLink
                              to={child.url}
                              onClick={handleNavClick}
                              className="hover:bg-muted/50"
                              activeClassName="bg-muted text-primary font-medium"
                            >
                              <span>{t(child.key)}</span>
                            </NavLink>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  )}
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarSeparator />

        <div className="flex flex-col gap-2 px-2 py-2">
          {open && (
            <div className="truncate px-1 text-sm text-muted-foreground">
              {isImpersonating()
                ? impersonatedUserName
                : (currentUser && getDisplayName(currentUser)) || currentUser?.email || ""}
            </div>
          )}

          <div className={open ? "flex gap-2" : "flex flex-col items-center gap-2"}>
            <LanguageSwitcher />

            <Button
              variant="ghost"
              size={open ? "sm" : "icon"}
              onClick={handleLogout}
              className={open ? "flex-1 justify-start" : ""}
              title={!open ? tCommon("actions.logout") : undefined}
            >
              <LogOut className={open ? "mr-2 h-4 w-4" : "h-4 w-4"} />
              {open && <span>{tCommon("actions.logout")}</span>}
            </Button>
          </div>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
